import { useEffect, useState } from 'react'
import { Route, Routes, useNavigate, useSearchParams } from 'react-router'
import Account from './pages/Account'
import { useAuth } from './AuthProvider.ts'
import Home from './pages/Home'
import Assessments from './pages/Assessments'
import Billing from './pages/Billing'
import OrganizationOverview from './pages/Organization/Overview.tsx'
import Employees from './pages/Employees'
import AssessmentDetail from './pages/Assessments/Detail.tsx'
import AssessmentSurvey from './pages/Assessments/Survey.tsx'
import { MeContext } from './MeProvider.ts'
import EmployeeDetail from './pages/Employees/Detail.tsx'
import FullPageLoader from './common/FullPageLoader'
import { useGetMeV2, useGetUserOrganizationRolesV2 } from './http/hooks.ts'
import EmployeesList from './pages/Organization/EmployeesList.tsx'
import Positions from './pages/Positions'
import InvitesList from './pages/Organization/OrganizationInvites.tsx'
import PositionDetail from './pages/Positions/Detail.tsx'
import AppLayout from './layout/AppLayout.tsx'
import { ScrollToTop } from './common/ScrollToTop.tsx'
import Elements from './pages/Elements/index.tsx'
import { useNextPeakClient } from './services/nextpeakClient/clientProvider.tsx'
import Statistics from './pages/Organization/Statistics';

function App() {
	const [searchParams] = useSearchParams()
	const [user, setUser] = useState<any | undefined>()
	const [roleArray, setRoleArray] = useState<string[]>([])
	const { authUser } = useAuth()
	const nextPeakClient = useNextPeakClient()
	const navigate = useNavigate() // Hook for programmatic navigation

	const { data: me, isLoading } = useGetMeV2(nextPeakClient)
	const { data: roleData, isLoading: roleLoader, error: roleError } = useGetUserOrganizationRolesV2(nextPeakClient, me?.id)

	useEffect(() => {
		const redirect = searchParams.get('redirect')
		localStorage.removeItem('postLoginRedirect')
		if (redirect) {
			navigate(redirect)
		}
	}, [])

	useEffect(() => {
		if (roleData) {
			const orgRolesArray = roleData.map((item: any) => item.name)
			setRoleArray(orgRolesArray)
		}
		if (roleError) {
			console.error(roleError)
		}
	}, [roleData, roleLoader, roleError])

	useEffect(() => {
		me && setUser(me)
	}, [me, authUser])

	// Redirect to /account if authUser.name is null
	useEffect(() => {
		if (!authUser?.name) {
			navigate('/account')
		}
	}, [authUser, navigate])

	if (!user || isLoading) {
		return <FullPageLoader />
	}

	return (
		<MeContext.Provider
			value={{
				id: user?.id,
				email: user?.email,
				name: user?.name,
				organizations: user?.organizations ?? [],
				organizationRoles: roleArray,
				userPosition: user?.userPosition,
				activeOrg: user?.organization,
			}}
		>
			<ScrollToTop />
			<Routes>
				<Route element={<AppLayout />}>
					{(roleArray.includes('admin') || roleArray.includes('billing')) && <Route path="/billing" element={<Billing />} />}
					{roleArray.includes('member') && <Route path="/assessments" element={<Assessments />} />}
					{roleArray.includes('member') && <Route path="/assessments/:assessmentId" element={<AssessmentDetail />} />}
					{roleArray.includes('member') && <Route path="/assessments/:assessmentId/responses/:responseId" element={<AssessmentSurvey />} />}
					<Route path="/account" element={<Account />} />
					{roleArray.includes('member') && <Route path="/employees" element={<Employees />} />}
					{roleArray.includes('member') && <Route path="/employees/:employeeId" element={<EmployeeDetail />} />}
					{/*{roleArray.includes('employee') && (*/}
					{/*	<Route*/}
					{/*		path="/organization/settings"*/}
					{/*		element={*/}
					{/*			<>*/}
					{/*				<PageTitle title="Organization | Surveyjs" />*/}
					{/*				<OrganizationSettings />*/}
					{/*			</>*/}
					{/*		}*/}
					{/*	/>*/}
					{/*)}*/}
					{roleArray.includes('member') && <Route path="/organization/graph" element={<OrganizationOverview />} />}
					{roleArray.includes('member') && <Route path="/organization/overview" element={<EmployeesList />} />}
					{roleArray.includes('member') && <Route path="/organization/invites" element={<InvitesList />} />}
					{roleArray.includes('admin') && <Route path="/organization/statistics" element={<Statistics />} />}
					<Route path="/positions" element={<Positions />} />
					<Route path="/elements" element={<Elements />} />
					<Route path="/positions/:positionId" element={<PositionDetail />} />
					<Route index element={<Home />} />
				</Route>
			</Routes>
		</MeContext.Provider>
	)
}

export default App
